import {
  IFeatureStyle,
  IFeatureStyleRule,
} from "@inovitas/infra3dsdk/dist/types/mm/featuretree/interfaces/IFeatureStyle";
import layerShapes from "./layerShapes";
import {
  PolygonSymbol,
  PointSymbol,
  LineSymbol,
  MultiPointSymbol,
  MultiLineSymbol,
  MultiPolygonSymbol,
} from "../../../../../../../common/utils/icons/icons";
import { mm } from "@inovitas/infra3dsdk";
import { defaultStore } from "../../../../../../../common/stores/defaultStore";
import { getGeometryTypeEnum } from "../../../../../../../common/utils/layers/layerutils";

function rgbaToString(color: number[]): string {
  const [r, g, b, a = 1] = color;
  return `rgba(${Math.round(r * 255)},${Math.round(g * 255)},${Math.round(b * 255)},${a})`;
}

function getRuleColor(rule: IFeatureStyleRule): string {
  switch (rule.symbolizer) {
    case "point":
    case "line":
      return rule.params.stroke
        ? rgbaToString(rule.params.stroke)
        : "transparent";
    case "polygon":
      return "fill" in rule.params && rule.params.fill
        ? rgbaToString(rule.params.fill)
        : "transparent";
  }
}

function matchesFilter(filter: string, properties: string): boolean {
  if (!filter || filter.trim() === "") return true;
  return filter === properties;
}

function findMatchingRule(
  rules: IFeatureStyleRule[],
  symbolizer: string,
  properties: string
): IFeatureStyleRule | undefined {
  const candidates = rules.filter((r) => r.symbolizer === symbolizer);

  const specific = candidates.find(
    (r) =>
      r.filter && r.filter.trim() !== "" && matchesFilter(r.filter, properties)
  );
  if (specific) return specific;

  return candidates.find((r) => !r.filter || r.filter.trim() === "");
}

interface LayerSymbolizerProps {
  style: IFeatureStyle;
  icon?: string | mm.GEOMETRYTYPE;
  /** Feature attribute values used to evaluate filter expressions */
  properties?: string;
}

export function LayerSymbolizer({
  style,
  icon,
  properties = "",
}: LayerSymbolizerProps) {
  const darkTheme = defaultStore.getState().darkTheme;

  const rules = style.rules;

  const line = findMatchingRule(rules, "line", properties);
  const lineColor = line ? getRuleColor(line) : "transparent";

  const point = findMatchingRule(rules, "point", properties);
  const pointColor = point ? getRuleColor(point) : "transparent";

  const polygon = findMatchingRule(rules, "polygon", properties);
  const polygonColor = polygon ? getRuleColor(polygon) : "transparent";

  const type = getGeometryTypeEnum(icon);

  const shapes = layerShapes();

  const correctPointShape = shapes.find((item: any) => {
    if (
      point &&
      "font-marker" in point.params &&
      "name" in point.params["font-marker"]
    ) {
      return item.value === point.params["font-marker"].name;
    }
    return false;
  });

  const correctPointShapeWithColor = correctPointShape
    ? correctPointShape.customizeIcon(pointColor, 20 + "px")
    : shapes
        .find((item: any) => item.value === "circle")
        ?.customizeIcon(pointColor, 20 + "px");

  const correctMultipointShapeWithColor = correctPointShape
    ? correctPointShape?.customizeIcon(pointColor, 8 + "px")
    : shapes
        .find((item: any) => item.value === "circle")
        ?.customizeIcon(pointColor, 20 + "px");

  const billboardBase64 =
    point && "billboard" in point.params
      ? point.params["billboard"]
      : undefined;

  return (
    <div
      style={{
        position: "relative",
        width: 20,
        height: 20,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: darkTheme ? "white" : "transparent",
        borderRadius: "4px",
      }}
    >
      {type === mm.GEOMETRYTYPE.LINESTRING && (
        <LineSymbol lineColor={lineColor} pointColor={pointColor} />
      )}
      {type === mm.GEOMETRYTYPE.MULTILINESTRING && (
        <MultiLineSymbol lineColor={lineColor} pointColor={pointColor} />
      )}
      {type === mm.GEOMETRYTYPE.POLYGON && (
        <PolygonSymbol
          lineColor={lineColor}
          polygonColor={polygonColor}
          pointColor={pointColor}
        />
      )}
      {type === mm.GEOMETRYTYPE.MULTIPOLYGON && (
        <MultiPolygonSymbol
          lineColor={lineColor}
          polygonColor={polygonColor}
          pointColor={pointColor}
        />
      )}
      {type === mm.GEOMETRYTYPE.POINT && (
        <PointSymbol
          pointColor={pointColor}
          pointShape={correctPointShapeWithColor}
          billboardBase64={billboardBase64}
        />
      )}
      {type === mm.GEOMETRYTYPE.MULTIPOINT && (
        <MultiPointSymbol
          pointColor={pointColor}
          pointShape={correctMultipointShapeWithColor}
        />
      )}
    </div>
  );
}
